import { Alert, Badge, cn, Input } from "@gotts.ai/ui";
import { ArrowLeft, Eye, Pencil, Search } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { PcbCard } from "../../components/PcbCard";
import { useDeployment, useWallet } from "../../context";

import { ContractEmptyState } from "./ContractEmptyState";
import { ContractHeader } from "./ContractHeader";
import { ContractSelector } from "./ContractSelector";
import { CustomContractForm } from "./CustomContractForm";
import { FunctionRow } from "./FunctionRow";
import { FunctionSection } from "./FunctionSection";
import { buildContractOptions, KNOWN_ABIS } from "./types";
import { useContractFavorites } from "./useContractFavorites";
import { useFunctionHistory } from "./useFunctionHistory";
import { useKnownAddresses } from "./useKnownAddresses";
import { extractFunctions, fnSigKey, isViewFunction } from "./utils";

import type { ContractOption, FunctionResult } from "./types";
import type { Address } from "viem";

export default function ContractsPage() {
  const { deployment } = useDeployment();
  const { address: walletAddress } = useWallet();

  const [selectedContract, setSelectedContract] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [customAddress, setCustomAddress] = useState("");
  const [customAbiText, setCustomAbiText] = useState("");
  const [fnSearch, setFnSearch] = useState("");

  const { favorites, toggleFavorite, isFavorite } = useContractFavorites();
  const { getHistory, addResult } = useFunctionHistory();
  const knownAddresses = useKnownAddresses();

  // Build options from deployment
  const contractOptions = useMemo<ContractOption[]>(() => {
    if (!deployment) return [];
    return buildContractOptions(deployment);
  }, [deployment]);

  // Selected contract details
  const selectedOpt = contractOptions.find((o) => o.value === selectedContract);

  const contractAddress = useMemo((): Address | null => {
    if (showCustom && customAddress) {
      return customAddress as Address;
    }
    return selectedOpt?.address ?? null;
  }, [showCustom, customAddress, selectedOpt]);

  const abi = useMemo((): readonly unknown[] | null => {
    if (showCustom && customAbiText) {
      try {
        return JSON.parse(customAbiText) as readonly unknown[];
      } catch {
        return null;
      }
    }
    if (selectedOpt?.abiKey && KNOWN_ABIS[selectedOpt.abiKey]) {
      return KNOWN_ABIS[selectedOpt.abiKey] ?? null;
    }
    return null;
  }, [showCustom, customAbiText, selectedOpt]);

  const functions = useMemo(() => {
    if (!abi) return { read: [], write: [] };
    const all = extractFunctions(abi);
    return {
      read: all.filter(isViewFunction),
      write: all.filter((f) => !isViewFunction(f)),
    };
  }, [abi]);

  const filteredRead = useMemo(
    () =>
      functions.read.filter(
        (fn) =>
          !fnSearch || fn.name.toLowerCase().includes(fnSearch.toLowerCase()),
      ),
    [functions.read, fnSearch],
  );

  const filteredWrite = useMemo(
    () =>
      functions.write.filter(
        (fn) =>
          !fnSearch || fn.name.toLowerCase().includes(fnSearch.toLowerCase()),
      ),
    [functions.write, fnSearch],
  );

  const handleSelect = useCallback((value: string) => {
    setSelectedContract(value);
    setShowCustom(false);
    setFnSearch("");
  }, []);

  const historyKey = useCallback(
    (fnName: string) => `${contractAddress ?? "unknown"}:${fnName}`,
    [contractAddress],
  );

  const handleResult = useCallback(
    (fnName: string, result: FunctionResult) => {
      addResult(historyKey(fnName), result);
    },
    [addResult, historyKey],
  );

  const hasAbi = abi !== null;
  const hasFunctions = functions.read.length > 0 || functions.write.length > 0;
  const showFunctions = hasAbi && hasFunctions && contractAddress;
  const hasSelection = !!selectedContract || showCustom;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-4">
        {hasSelection && (
          <button
            type="button"
            onClick={() => {
              setSelectedContract("");
              setShowCustom(false);
              setFnSearch("");
            }}
            className="octa-sm flex h-8 w-8 items-center justify-center bg-[rgba(255,255,255,0.04)] text-[var(--color-text-muted)] transition-all hover:bg-[rgba(201,168,76,0.1)] hover:text-[var(--color-gold)]"
          >
            <ArrowLeft size={14} />
          </button>
        )}
        <div>
          {hasSelection && (
            <h1 className="font-sans text-lg font-semibold text-[var(--color-text)]">
              {selectedOpt?.label ?? "Custom Contract"}
            </h1>
          )}
        </div>
        {hasSelection && selectedOpt && (
          <div className="ml-auto flex items-center gap-2">
            <Badge
              className={cn(
                "text-[9px]",
                functions.read.length > 0
                  ? "bg-green-500/20 text-green-400"
                  : "bg-white/5 text-white/30",
              )}
            >
              <Eye size={9} className="mr-1 inline" />
              {functions.read.length} read
            </Badge>
            <Badge
              className={cn(
                "text-[9px]",
                functions.write.length > 0
                  ? "bg-amber-500/20 text-amber-400"
                  : "bg-white/5 text-white/30",
              )}
            >
              <Pencil size={9} className="mr-1 inline" />
              {functions.write.length} write
            </Badge>
          </div>
        )}
      </div>

      {/* Empty state — no contract selected */}
      {!hasSelection && (
        <>
          <ContractSelector
            options={contractOptions}
            selected={selectedContract}
            onSelect={handleSelect}
            favorites={favorites}
            onToggleFavorite={toggleFavorite}
            onCustom={() => {
              setShowCustom(true);
              setSelectedContract("");
            }}
          />
          <ContractEmptyState
            options={contractOptions}
            favorites={favorites}
            onSelect={handleSelect}
          />
        </>
      )}

      {/* Custom contract form */}
      {showCustom && (
        <CustomContractForm
          address={customAddress}
          abiText={customAbiText}
          onAddressChange={setCustomAddress}
          onAbiChange={setCustomAbiText}
          onClose={() => {
            setShowCustom(false);
            setCustomAddress("");
            setCustomAbiText("");
          }}
        />
      )}

      {/* Contract header + functions */}
      {selectedOpt && contractAddress && (
        <div className="space-y-5">
          <ContractHeader
            contract={selectedOpt}
            walletAddress={walletAddress}
            isFavorite={isFavorite(selectedOpt.value)}
            onToggleFavorite={() => {
              toggleFavorite(selectedOpt.value);
            }}
          />

          {/* No ABI warning */}
          {!hasAbi && !showCustom && (
            <Alert variant="warning" title="Limited ABI">
              No detailed ABI available for this contract. Try the custom option
              with a pasted ABI.
            </Alert>
          )}

          {/* Function search */}
          {showFunctions && (
            <div className="relative">
              <Search
                size={13}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
              />
              <Input
                type="text"
                placeholder="Filter functions..."
                value={fnSearch}
                onChange={(e) => {
                  setFnSearch(e.target.value);
                }}
                className="pl-8"
              />
            </div>
          )}

          {/* Read + Write function sections side by side at wider viewports */}
          {showFunctions && (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {filteredRead.length > 0 && (
                <FunctionSection type="read" count={functions.read.length}>
                  {(allCollapsed) =>
                    filteredRead.map((fn) => (
                      <FunctionRow
                        key={fnSigKey(fn)}
                        fn={fn}
                        contractAddress={contractAddress}
                        abi={abi}
                        isWrite={false}
                        knownAddresses={knownAddresses}
                        history={getHistory(historyKey(fn.name))}
                        onResult={(r) => {
                          handleResult(fn.name, r);
                        }}
                        forceCollapsed={allCollapsed}
                        abiKey={selectedOpt.abiKey}
                      />
                    ))
                  }
                </FunctionSection>
              )}

              {filteredWrite.length > 0 && (
                <FunctionSection type="write" count={functions.write.length}>
                  {(allCollapsed) =>
                    filteredWrite.map((fn) => (
                      <FunctionRow
                        key={fnSigKey(fn)}
                        fn={fn}
                        contractAddress={contractAddress}
                        abi={abi}
                        isWrite={true}
                        knownAddresses={knownAddresses}
                        history={getHistory(historyKey(fn.name))}
                        onResult={(r) => {
                          handleResult(fn.name, r);
                        }}
                        forceCollapsed={allCollapsed}
                        abiKey={selectedOpt.abiKey}
                      />
                    ))
                  }
                </FunctionSection>
              )}
            </div>
          )}

          {/* No functions warning */}
          {hasAbi && !hasFunctions && (
            <PcbCard label="ABI" cut={8}>
              <p className="py-4 text-center text-xs text-[var(--color-text-muted)]">
                This ABI contains no callable functions.
              </p>
            </PcbCard>
          )}
        </div>
      )}
    </div>
  );
}
